import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

interface Row { id: string; full_name: string; email: string | null; status: string }

interface Props {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  excludeId: string | null;
  title?: string;
  onPick: (id: string, row: Row) => void;
}

export default function InstructorPickerDialog({ open, onOpenChange, excludeId, title, onPick }: Props) {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      const term = q.trim();
      let query = (supabase as any).from("instructors")
        .select("id,full_name,email,status")
        .neq("status", "merged")
        .order("full_name")
        .limit(25);
      if (term.length >= 2) query = query.or(`full_name.ilike.%${term}%,email.ilike.%${term}%,preferred_name.ilike.%${term}%`);
      const { data } = await query;
      if (cancelled) return;
      setRows(((data ?? []) as Row[]).filter((r) => r.id !== excludeId));
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [open, q, excludeId]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{title ?? "Select the record to keep (Primary)"}</DialogTitle>
          <DialogDescription>Search by name or email. Merged records are hidden.</DialogDescription>
        </DialogHeader>
        <Input placeholder="Search instructors…" value={q} onChange={(e) => setQ(e.target.value)} autoFocus />
        <div className="max-h-72 overflow-y-auto border rounded">
          {loading ? (
            <div className="p-3 text-xs text-muted-foreground">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="p-3 text-xs text-muted-foreground">No matches.</div>
          ) : rows.map((r) => (
            <button key={r.id} type="button"
              onClick={() => { onPick(r.id, r); onOpenChange(false); }}
              className="w-full text-left px-3 py-2 hover:bg-accent border-b last:border-b-0">
              <div className="text-sm font-medium">{r.full_name}</div>
              <div className="text-xs text-muted-foreground">{r.email || "—"} · {r.status}</div>
            </button>
          ))}
        </div>
        <div className="flex justify-end">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
